import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DetailBox } from 'common/components/DetailBox';
import {
    IUserType,
    useForgotPasswordMutation,
    useUserLazyQuery,
} from 'graphql/types';
import { useSnackbarContext } from 'common/context';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

import { UserForm } from '../forms/UserForm';

import { DetailView } from './DetailView';

type TFullUserType = Pick<
    IUserType,
    | 'id'
    | 'firstName'
    | 'lastNamePrefix'
    | 'lastName'
    | 'email'
    | 'jobTitle'
    | 'phone'
    | 'isMainUser'
    | 'locale'
>;

interface IProps {
    basic?: boolean;
    className?: string;
    userData?: {
        user?: TFullUserType | null;
        loading: boolean;
        error?: ApolloError;
    };
}

const BasePersonalDetailBox = ({ basic, className, userData }: IProps) => {
    const { setSnackbarState } = useSnackbarContext();
    const [isEditing, setIsEditing] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const { t } = useTranslation();

    const [fetchUser, { data, loading: loadingUser, error: errorUser }] =
        useUserLazyQuery({
            onError: (error) => {
                setSnackbarState({
                    message: error.message,
                    variant: 'error',
                });
            },
        });
    const [resetPassword] = useForgotPasswordMutation({
        update: () => {
            setSnackbarState({
                message: t('messages.passwordResetEmailSent'),
                variant: 'success',
            });

            setShowPasswordResetModal(false);
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    useEffect(() => {
        if (!userData) return fetchUser();
    }, [userData]);

    const user = userData ? userData.user : data?.user;
    const loading = userData ? userData.loading : loadingUser;
    const error = userData ? userData.error : errorUser;

    const handlePasswordReset = () => {
        if (!user) return;

        resetPassword({ variables: { email: user.email } });
    };

    let content;
    if (user) {
        content = isEditing ? (
            <UserForm
                user={user}
                onClose={(snackbarState) => {
                    if (snackbarState) {
                        setSnackbarState(snackbarState);

                        if (snackbarState.variant === 'error') return;
                    }

                    setIsEditing(false);
                }}
            />
        ) : (
            <DetailView
                basic={basic}
                user={user}
                onEditClick={() => setIsEditing(true)}
                onPasswordResetClick={() => setShowPasswordResetModal(true)}
            />
        );
    }

    return (
        <DetailBox
            className={className}
            error={!!error}
            loading={loading}
            showTitleDivider={!basic}
            title={t('user.personalInformation')}
        >
            <ConfirmDialog
                cancelProps={{
                    text: t('dialogs.back'),
                    onClick: () => setShowPasswordResetModal(false),
                }}
                confirmProps={{
                    text: t('dialogs.editPassword.confirm'),
                    onClick: handlePasswordReset,
                }}
                open={showPasswordResetModal}
                title={t('dialogs.editPassword.title')}
            >
                {t('dialogs.editPassword.message')}
            </ConfirmDialog>
            {content}
        </DetailBox>
    );
};

export const PersonalDetailBox = styled(BasePersonalDetailBox)``;
